import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import { Plus, Trash2, Tag, Layers } from 'lucide-react';

export default function AdminCategories() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '' });
  
  const showConfirm = (title, message, action) => setConfirmModal({ open: true, title, message, action });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '' });
  
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => api.get('/categories').then(r => setCategories(r.data));

  useEffect(() => { load(); }, []);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    setLoading(true);
    try {
      await api.post('/categories', { name: newCat.trim() });
      setNewCat('');
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Kategori eklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = (category) => {
    setConfirmModal({
      open: true,
      title: 'Kategoriyi Sil',
      message: `"${category.name}" kategorisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      type: 'danger',
      requireText: true,
      action: async () => {
        try {
          await api.delete('/categories/' + category.id);
          load();
        } catch (e) {
          alert(e.response?.data?.error || 'Kategori silinemedi.');
        }
        closeConfirm();
      }
    });
  };

  return (
    <AdminLayout>
      <div className="page-container">
        
        {/* Sayfa Başlığı */}
        <div className="page-header">
          <div>
            <h2 className="page-title">Kategori Yönetimi</h2>
            <p className="page-subtitle">Eğitimlerinizi sınıflandırmak için kategoriler oluşturun ve düzenleyin.</p>
          </div>
        </div>

        <div className="grid-layout">
          {/* Sol Kolon: Yeni Ekle */}
          <div className="content-card p-5 h-fit">
            <div className="card-heading d-flex align-center gap-2 mb-4">
              <Plus size={18} className="text-red" strokeWidth={3} /> 
              <span>Yeni Kategori Ekle</span>
            </div>
            
            {/* Input alanı ve altındaki boşluk artırıldı */}
            <div className="form-group" style={{ marginBottom: '28px' }}>
              <label className="form-label">KATEGORİ ADI</label>
              <input
                value={newCat}
                onChange={e => setNewCat(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCategory()}
                placeholder="Örn: İş Güvenliği, Kişisel Gelişim..."
                className="form-input py-3"
              />
            </div>
            
            {/* Buton Kırmızı (btn-red) yapıldı */}
            <button 
              onClick={addCategory} 
              disabled={loading || !newCat.trim()}
              className="btn-red w-full py-3"
            >
              {loading ? 'Ekleniyor...' : 'Kategoriyi Ekle'}
            </button>
          </div>

          {/* Sağ Kolon: Kategori Listesi */}
          <div className="content-card">
            <div className="card-header d-flex align-center gap-2">
              <Layers size={18} className="text-primary" />
              <span className="card-heading m-0">Mevcut Kategoriler</span>
              <span className="badge bg-blue-light text-blue ml-auto">{categories.length} Adet</span>
            </div>

            <div className="category-list">
              {categories.length === 0 ? (
                <div className="empty-state">
                  <Tag size={40} className="text-muted mb-3 mx-auto" strokeWidth={1.5} />
                  Henüz hiç kategori oluşturulmamış.
                </div>
              ) : (
                categories.map((cat) => (
                  <div key={cat.id} className="category-list-item">
                    <div className="d-flex align-center gap-3">
                      <div className="category-icon-bg">
                        <Tag size={16} className="text-primary" />
                      </div>
                      <span className="font-medium text-dark">{cat.name}</span>
                    </div>
                    
                    {isAdmin && (
                      <button 
                        onClick={() => deleteCategory(cat)}
                        className="btn-icon-danger"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => { confirmModal.action && confirmModal.action(); }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
        requireText={confirmModal.requireText}
      />

      {/* CSS Stilleri */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

        * { -webkit-font-smoothing: antialiased; font-family: 'Poppins', sans-serif; }

        .page-container { animation: fadeSlideUp 0.4s ease-out; }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        /* Typography & Utils */
        .text-muted { color: #6b7280; }
        .text-dark { color: #111827; }
        .text-primary { color: #2563eb; }
        .text-blue { color: #2563eb; }
        .text-red { color: #e30613; }
        
        .font-medium { font-weight: 500; }
        .fw-bold { font-weight: 700; }
        
        .w-full { width: 100%; }
        .h-fit { height: fit-content; }
        .ml-auto { margin-left: auto; }
        .mx-auto { margin-left: auto; margin-right: auto; }
        
        .d-flex { display: flex; }
        .align-center { align-items: center; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 12px; }
        .m-0 { margin: 0; }
        .mb-3 { margin-bottom: 12px; }
        .mb-4 { margin-bottom: 16px; }
        .p-5 { padding: 24px; }
        .py-3 { padding-top: 12px; padding-bottom: 12px; }

        .bg-blue-light { background: #eff6ff; }

        /* Header */
        .page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
        .page-title { margin: 0 0 4px 0; font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -0.5px; }
        .page-subtitle { margin: 0; font-size: 13.5px; color: #6b7280; }

        /* Layout Grid */
        .grid-layout { display: grid; grid-template-columns: 1fr; gap: 20px; align-items: flex-start; }
        @media (min-width: 1024px) { .grid-layout { grid-template-columns: 380px 1fr; } }

        /* Cards & Form */
        .content-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); overflow: hidden; }
        .card-header { padding: 16px 24px; border-bottom: 1px solid #eaecf0; background: #fcfcfd;}
        .card-heading { font-weight: 700; font-size: 16px; color: #111827; }

        .form-group { margin-bottom: 0; }
        .form-label { display: block; margin-bottom: 8px; font-weight: 600; font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;}
        .form-input { width: 100%; padding: 10px 14px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; box-sizing: border-box; background: white; color: #1e293b;}
        .form-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px #eff6ff; }

        /* Category List */
        .category-list { max-height: 500px; overflow-y: auto; }
        .category-list-item { padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #f2f4f7; transition: background 0.2s; background: white;}
        .category-list-item:last-child { border-bottom: none; }
        .category-list-item:hover { background: #f9fafb; }
        
        .category-icon-bg { width: 36px; height: 36px; border-radius: 10px; background: #eff6ff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: transform 0.2s; }
        .category-list-item:hover .category-icon-bg { transform: scale(1.1) rotate(5deg); }

        /* Buttons & Badges */
        .btn-red { background: #e30613; color: white; border: 1px solid #e30613; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
        .btn-red:hover:not(:disabled) { background: #ba000a; border-color: #ba000a; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(227, 6, 19, 0.2); }
        .btn-red:disabled { opacity: 0.6; cursor: not-allowed; background: #94a3b8; border-color: #94a3b8;}
        
        .btn-icon-danger { background: #fef2f2; color: #ef4444; border: 1px solid #fee2e2; border-radius: 8px; padding: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .btn-icon-danger:hover { background: #fee2e2; border-color: #fca5a5; }

        .badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; white-space: nowrap;}

        .empty-state { text-align: center; padding: 60px 20px !important; color: #6b7280; font-size: 14px; display: flex; flex-direction: column; align-items: center;}
      `}</style>
    </AdminLayout>
  );
}
